import React, {useEffect, useRef, useState} from 'react';
import {Card, Col, Row} from 'react-bootstrap';
import Chart from 'react-apexcharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import ApiUtils from 'api/ApiUtils';
import {toast} from 'react-toastify';
import Select from 'react-select';

const VenuePerformance = (): any => {
  const [startDate, setStartDate] = useState(new Date());
  const selectedYear = moment(startDate).format('YYYY');
  const [cardData, setCardData]: any = useState([]);
  const [venues, setVenues]: any = useState([]);

  const venueList: any = [];
  venues?.map((venue: any): any =>
    venueList.push({value: venue.id, label: venue.name}),
  );

  const [selectedVenue, setSelectedVenue] = useState<any>();

  const [activeChart, setActiveChart] = useState<string>('');
  const inputRef: any = useRef(null);
  const inputRefByYear: any = useRef(null);

  const fetchDasboardData = async (): Promise<void> => {
    if (selectedVenue !== undefined) {
      const resp: any = await ApiUtils.getVenuePerformanceData(
        `?year=${selectedYear}&venue_id=${selectedVenue?.value}`,
      );

      setCardData(resp?.data);
    }
  };

  useEffect(() => {
    void fetchDasboardData();
  }, [startDate, selectedVenue]);

  const series = [
    {
      name: `Guest List - ${selectedYear}`,
      data: cardData?.map((data: any) => {
        return data.guest_count;
      }),
    },
    {
      name: 'Table Booked - 2013',
      data: cardData?.map((data: any) => {
        return data.table_count;
      }),
    },
  ];

  const options: any = {
    chart: {
      height: 350,
      type: 'line',
      dropShadow: {
        enabled: true,
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 0.5,
      },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: ['#77B6EA', '#545454'],
    dataLabels: {
      enabled: true,
    },
    stroke: {
      curve: 'smooth',
    },
    title: {
      text: `Guest list & Table booking for ${selectedVenue?.label} - Year ${selectedYear} `,
      align: 'left',
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
        opacity: 0.5,
      },
    },
    markers: {
      size: 1,
    },
    xaxis: {
      categories: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ],
      title: {
        text: 'Month',
      },
    },
    yaxis: {
      title: {
        text: 'Perfomance',
      },
      // min: 5,
      // max: 40,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5,
    },
  };

  useEffect(() => {
    if (inputRef?.current != null) {
      inputRef?.current?.setFocus();
    }
    if (inputRefByYear?.current != null) {
      inputRefByYear?.current?.setFocus();
    }
  }, [activeChart]);

  const handleDownloadByYear = async (dateString: any): Promise<void> => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    try {
      const response: any = await ApiUtils.downloadSalesReport(`year=${year}`);

      if (response?.data.report_file_url !== '') {
        toast.success(response.message);
        window.open(response.data.report_file_url, '_blank');
      } else {
        toast.error('Report not available');
      }

      setActiveChart('');
    } catch (error: any) {
      toast.error(error.message);
    }
  };
  const handleDownloadByMonth = async (dateString: any): Promise<void> => {
    const date = new Date(dateString);

    const month = date.getMonth() + 1;
    try {
      const response: any = await ApiUtils.downloadSalesReport(
        `month=${month}`,
      );
      // window.open(response.data.report_file_url, '_blank');
      if (response?.data.report_file_url !== '') {
        toast.success(response.message);
        window.open(response.data.report_file_url, '_blank');
      } else {
        toast.error('Report not available');
      }

      setActiveChart('');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleVenueSelect = (venue: any): any => {
    setSelectedVenue(venue);
  };

  useEffect(() => {
    ApiUtils.getVenueList(`?type=dropDown`)
      .then((response: any) => {
        console.log('venues:', response);
        setVenues(response);

        // ✅ Set selectedVenue to the first item in venues array
        if (response?.length > 0) {
          setSelectedVenue({
            value: response?.[0].id,
            label: response?.[0].name,
          });
        }
      })
      .catch(error => {
        toast.error(error.message);
      });
  }, []);

  return (
    <Col xxl={12} className="order-last">
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          {/* Left: Title */}

          <h4 className="card-title mb-0 text-start flex-grow-1">
            Venue Performance
          </h4>

          {/* Middle: Venue Selector & Year Picker */}
          <div className="d-flex align-items-center gap-3">
            {/* Venue Dropdown */}
            <div className="input-group">
              {venueList?.[0]?.value !== undefined && (
                <Select
                  // defaultValue={venueList?.[0]}
                  defaultValue={venueList?.[0]}
                  options={venueList}
                  onChange={handleVenueSelect}
                  placeholder="Select Venue"
                />
              )}
            </div>

            {/* Year Picker - Ensuring it stays in 1 row */}
            <div
              className="d-flex align-items-center gap-2"
              data-testid="year-picker">
              <DatePicker
                selected={startDate}
                onChange={(date: any) => {
                  setStartDate(date);
                }}
                showYearPicker
                dateFormat="yyyy"
                className="form-control"
              />
              <div
                className="input-group-text bg-primary border-primary text-white"
                style={{borderRadius: '0.15rem', cursor: 'pointer'}}>
                <i className="ri-calendar-2-line"></i>
              </div>
            </div>
          </div>

          {/* Right: Conditional Month/Year Picker */}
          <div className="d-flex gap-2">
            {activeChart === 'monthly' && (
              <div className="date-and-month-picker">
                <DatePicker
                  ref={inputRef}
                  selected={startDate}
                  onChange={handleDownloadByMonth}
                  showMonthYearPicker
                  dateFormat="M"
                  className="form-control"
                />
              </div>
            )}

            {activeChart === 'year' && (
              <div className="date-and-month-picker">
                <DatePicker
                  ref={inputRefByYear}
                  selected={startDate}
                  onChange={handleDownloadByYear}
                  showYearPicker
                  dateFormat="yyyy"
                  className="form-control"
                />
              </div>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xxl={12}>
              <Chart
                options={options}
                series={series}
                type="line"
                height="405"
                className="apex-charts"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default VenuePerformance;
